import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { fiveStarApi } from '../lib/api';
import { Shield, UserCog, FileText, BarChart3, Plus, Trash2, Search, AlertCircle, Crown, FileEdit, X, Star, RefreshCw, Download, Activity, Bell, UserPlus, Eye, Clock, ChevronDown, ChevronRight, Mail, CalendarClock } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { fmtDate } from '../lib/adminApi';
import { AnalyticsHubTab } from './admin/hub-tabs/AnalyticsHubTab';
import { ActivityHubTab } from './admin/hub-tabs/ActivityHubTab';
import { InvitesHubTab } from './admin/hub-tabs/InvitesHubTab';
import { ImpersonateHubTab } from './admin/hub-tabs/ImpersonateHubTab';
import { WhatsNewHubTab } from './admin/hub-tabs/WhatsNewHubTab';
import { TahTemplateSection } from './admin/hub-tabs/TahTemplateSection';
import { MonthPreviewHubTab } from './admin/hub-tabs/MonthPreviewHubTab';
import EmployeeTypeahead from '../components/EmployeeTypeahead';
import { FiveStarCategoriesSection } from './admin/hub-tabs/FiveStarCategoriesSection';

const ROLE_META = {
  super_admin: { label: 'Super Admin', icon: Crown, color: 'bg-violet-50 text-violet-700 border-violet-200', desc: 'Full control: grants/revokes roles, manages everything.' },
  template_admin: { label: 'Template Admin', icon: FileEdit, color: 'bg-cyan-50 text-cyan-700 border-cyan-200', desc: 'Manages the facility template used by all tacticals.' },
  reports_admin: { label: 'Reports Admin', icon: BarChart3, color: 'bg-emerald-50 text-emerald-700 border-emerald-200', desc: 'Views PowerBI connection details and report endpoints.' },
  review_initiator: { label: 'Review Initiator', icon: Star, color: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200', desc: 'Can initiate 5-Star reviews and be selected as an Area Manager.' },
};

// Lazy import of admin role API (defined inline since adminApi.* doesn't exist; we use fiveStarApi import to reuse axios). Actually the role API was added to adminApi — import it directly.
// Use a small adapter to access the new role endpoints via fetch with the user JWT.
const apiBase = () => process.env.REACT_APP_BACKEND_URL;
const tok = () => localStorage.getItem('tactical_token');
const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${tok()}`,
});

const roleApi = {
  me: () => fetch(`${apiBase()}/api/admin-roles/me`, { headers: authHeaders() }).then(r => r.json()),
  users: () => fetch(`${apiBase()}/api/admin-roles/users`, { headers: authHeaders() }).then(r => r.json()),
  search: (q) => fetch(`${apiBase()}/api/admin-roles/searchable-users?q=${encodeURIComponent(q || '')}`, { headers: authHeaders() }).then(r => r.json()),
  grant: (userId, role) =>
    fetch(`${apiBase()}/api/admin-roles/grant`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ user_id: userId, role })
    }).then(async r => { if (!r.ok) throw new Error((await r.json()).detail || 'Failed'); return r.json(); }),
  revoke: (userId, role) =>
    fetch(`${apiBase()}/api/admin-roles/${userId}/${role}`, { method: 'DELETE', headers: authHeaders() })
      .then(async r => { if (!r.ok) throw new Error((await r.json()).detail || 'Failed'); return r.json(); }),
  getTemplate: () => fetch(`${apiBase()}/api/admin-roles/templates/facility`, { headers: authHeaders() }).then(r => r.json()),
  saveTemplate: (items) =>
    fetch(`${apiBase()}/api/admin-roles/templates/facility`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify({ items })
    }).then(async r => { if (!r.ok) throw new Error((await r.json()).detail || 'Failed'); return r.json(); }),
};

const RoleBadge = ({ role }) => {
  const meta = ROLE_META[role] || { label: role, icon: Shield, color: 'bg-slate-50 text-slate-700 border-slate-200' };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.color}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
};

// ============================ TABS ============================

const MyRolesTab = ({ myRoles }) => (
  <div data-testid="tab-my-roles" className="space-y-4">
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div className="text-sm font-bold text-slate-900 mb-1">Your Admin Access</div>
      <div className="text-xs text-slate-500 mb-4">Signed in as <strong className="text-slate-700">{myRoles?.email}</strong></div>
      {!myRoles?.roles?.length && (
        <div className="text-sm text-slate-500">You don't currently hold any admin role.</div>
      )}
      <div className="flex flex-wrap gap-2">
        {(myRoles?.roles || []).map(r => <RoleBadge key={r} role={r} />)}
      </div>
    </div>
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div className="text-sm font-bold text-slate-900 mb-3">Available Roles</div>
      <div className="space-y-2.5">
        {Object.entries(ROLE_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const has = (myRoles?.roles || []).includes(key);
          return (
            <div key={key} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
              <Icon className="w-4 h-4 text-slate-500 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-800">{meta.label} {has && <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-600 font-bold">Yours</span>}</div>
                <div className="text-xs text-slate-500">{meta.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

const UserRolesTab = () => {
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGrant, setShowGrant] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await roleApi.users();
      setAdminUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleRevoke = async (userId, role, email) => {
    if (!window.confirm(`Revoke ${ROLE_META[role]?.label || role} from ${email}?`)) return;
    try {
      await roleApi.revoke(userId, role);
      toast.success('Role revoked');
      reload();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div data-testid="tab-user-roles" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-slate-900">Admin Users</div>
          <div className="text-xs text-slate-500">Manage who has admin access and what roles they hold.</div>
        </div>
        <button
          onClick={() => setShowGrant(true)}
          data-testid="grant-role-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 shadow"
        >
          <Plus className="w-3.5 h-3.5" /> Grant Role
        </button>
      </div>

      {loading && <div className="text-sm text-slate-500 py-4">Loading…</div>}
      {!loading && adminUsers.length === 0 && (
        <div className="text-sm text-slate-500 py-4 bg-white border border-dashed rounded-xl text-center">
          No admin users yet.
        </div>
      )}
      {!loading && adminUsers.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold text-slate-700 text-xs uppercase tracking-wide">User</th>
                <th className="text-left px-4 py-2.5 font-semibold text-slate-700 text-xs uppercase tracking-wide">Roles</th>
                <th className="px-4 py-2.5 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {adminUsers.map(u => (
                <tr key={u.user_id} className="border-b last:border-b-0 border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{u.display_name}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {u.roles.map(r => (
                        <span key={r} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border border-slate-200 bg-white">
                          <RoleBadge role={r} />
                          <button
                            onClick={() => handleRevoke(u.user_id, r, u.email)}
                            title={`Revoke ${r}`}
                            data-testid={`revoke-${u.user_id}-${r}`}
                            className="text-slate-400 hover:text-rose-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showGrant && <GrantRoleModal onClose={() => setShowGrant(false)} onGranted={() => { setShowGrant(false); reload(); }} />}
    </div>
  );
};

const GrantRoleModal = ({ onClose, onGranted }) => {
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [role, setRole] = useState('template_admin');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (query.length < 2) { setUsers([]); return; }
      try {
        const data = await roleApi.search(query);
        setUsers(data?.users || []);
      } catch { setUsers([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const handleGrant = async () => {
    if (!selectedUser || !role) return;
    setSubmitting(true);
    try {
      await roleApi.grant(selectedUser.id, role);
      toast.success(`Granted ${ROLE_META[role]?.label} to ${selectedUser.email}`);
      onGranted();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="grant-role-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold">Grant Admin Role</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Search User</label>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={query}
                onChange={e => { setQuery(e.target.value); setSelectedUser(null); }}
                placeholder="Type name or email…"
                data-testid="grant-search-input"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-violet-400"
              />
            </div>
            {users.length > 0 && !selectedUser && (
              <div className="mt-2 border border-slate-200 rounded-md max-h-48 overflow-y-auto bg-white shadow-sm">
                {users.map(u => (
                  <button
                    key={u.id}
                    onClick={() => { setSelectedUser(u); setQuery(u.email); setUsers([]); }}
                    data-testid={`grant-user-${u.email}`}
                    className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b last:border-b-0 border-slate-100"
                  >
                    <div className="text-sm font-medium">{u.display_name}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </button>
                ))}
              </div>
            )}
            {selectedUser && (
              <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5">
                Selected: <strong>{selectedUser.display_name}</strong> ({selectedUser.email})
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Role</label>
            <div className="space-y-1.5">
              {Object.entries(ROLE_META).map(([key, meta]) => {
                const Icon = meta.icon;
                return (
                  <label key={key} className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition ${role === key ? 'border-violet-400 bg-violet-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                    <input
                      type="radio"
                      name="role"
                      checked={role === key}
                      onChange={() => setRole(key)}
                      data-testid={`grant-role-${key}`}
                      className="mt-1 accent-violet-600"
                    />
                    <Icon className="w-4 h-4 mt-0.5 text-slate-600" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{meta.label}</div>
                      <div className="text-xs text-slate-500">{meta.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-semibold rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
            <button
              onClick={handleGrant}
              disabled={!selectedUser || submitting}
              data-testid="grant-confirm-btn"
              className="px-4 py-2 text-sm font-semibold rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
            >
              {submitting ? 'Granting…' : 'Grant Role'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Collapsible card used to fold/unfold each template sub-section.
 * Remembers its open/closed state per browser via localStorage so admins
 * don't have to re-expand the section they were working in after a reload.
 */
const CollapsibleSection = ({ storageKey, title, subtitle, icon: Icon, badge, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return defaultOpen;
    const v = localStorage.getItem(`tmpl_section_open__${storageKey}`);
    return v === null ? defaultOpen : v === '1';
  });

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(`tmpl_section_open__${storageKey}`, next ? '1' : '0'); } catch (_e) { /* ignore */ }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`section-${storageKey}`}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        data-testid={`section-toggle-${storageKey}`}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
      >
        {open
          ? <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
          : <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />}
        {Icon && <Icon className="w-4 h-4 text-cyan-600 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
            {title}
            {badge != null && (
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold">
                {badge}
              </span>
            )}
          </div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100">
          {children}
        </div>
      )}
    </div>
  );
};

const TemplatesTab = () => {
  const [template, setTemplate] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await roleApi.getTemplate();
      setTemplate(data);
      setItems(data?.items || []);
    } catch (err) { toast.error('Failed to load template'); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  // Disable Add Item until initial load completes to avoid the "GET overwrites local add" race
  const canEdit = !loading;

  const updateItem = (i, field, value) => {
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  };
  const addItem = () => {
    if (!canEdit) return;
    setItems(prev => [...prev, { id: `tmpl_${Date.now()}_${Math.random().toString(36).slice(2,6)}`, question: '', value: '' }]);
  };
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));
  const moveItem = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setItems(copy);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await roleApi.saveTemplate(items);
      toast.success('Template saved — new tacticals will use these items');
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="tab-templates" className="space-y-3">
      <div className="px-1 pb-1 text-xs text-slate-500">
        Click any section header to expand or collapse it. Your last choice is remembered.
      </div>

      {/* ---------- Facility Overview ---------- */}
      <CollapsibleSection
        storageKey="facility"
        title="Facility Overview Template"
        subtitle="Questions pre-loaded on every new facility site in a tactical."
        icon={FileText}
        badge={items.length}
        defaultOpen
      >
        <div className="flex items-center justify-end gap-2 mb-3">
          <button onClick={addItem} disabled={!canEdit} data-testid="add-template-item" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-slate-300 hover:bg-slate-50 disabled:opacity-50">
            <Plus className="w-3.5 h-3.5" /> Add Item
          </button>
          <button onClick={handleSave} disabled={saving} data-testid="save-template-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Template'}
          </button>
        </div>

        {loading && <div className="text-sm text-slate-500 py-4">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl py-10 text-center text-sm text-slate-500">
            No items yet. Click "Add Item" to create the first one.
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={it.id || i} className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid={`template-item-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="flex flex-col gap-1 pt-1">
                    <button onClick={() => moveItem(i, -1)} className="text-xs text-slate-400 hover:text-slate-700" disabled={i === 0}>▲</button>
                    <button onClick={() => moveItem(i, 1)} className="text-xs text-slate-400 hover:text-slate-700" disabled={i === items.length - 1}>▼</button>
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      value={it.question || ''}
                      onChange={e => updateItem(i, 'question', e.target.value)}
                      placeholder="Question / prompt label"
                      className="w-full text-sm font-medium px-3 py-2 rounded-md border border-slate-200 bg-white focus:outline-none focus:border-cyan-400"
                    />
                    <textarea
                      value={it.value || ''}
                      onChange={e => updateItem(i, 'value', e.target.value)}
                      placeholder="Default answer (optional)"
                      rows={2}
                      className="w-full text-sm px-3 py-2 rounded-md border border-slate-200 bg-white focus:outline-none focus:border-cyan-400 resize-y"
                    />
                  </div>
                  <button onClick={() => removeItem(i)} title="Remove" className="text-slate-400 hover:text-rose-600 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* ---------- TAH (Therapy @Home) ---------- */}
      <CollapsibleSection
        storageKey="tah"
        title="TAH (Therapy @Home) Template"
        subtitle="Items shown in the Therapy @Home selector on every tactical."
        icon={FileText}
      >
        <TahTemplateSection />
      </CollapsibleSection>

      {/* ---------- 5-Star Categories ---------- */}
      <CollapsibleSection
        storageKey="five-star-cats"
        title="5-Star Review Categories"
        subtitle="Categories shown on every 5-Star review rating step."
        icon={Star}
      >
        <FiveStarCategoriesSection />
      </CollapsibleSection>
    </div>
  );
};

const ReportsTab = () => {
  const apiKey = 'Stored in backend/.env as POWERBI_API_KEY';
  const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/reports`;
  const endpoints = [
    { path: '/users', desc: 'All users in the system' },
    { path: '/tacticals', desc: 'All tactical headers (id, month, year, owner)' },
    { path: '/scorecard', desc: 'Scorecard entries flattened across tacticals' },
    { path: '/facility-items', desc: 'Facility overview Q&A across tacticals + sites' },
    { path: '/sub-items', desc: 'Sub-items (Lightning Round, Development Goals)' },
    { path: '/manager-relationships', desc: 'Approved manager/subordinate links' },
    { path: '/scorecard-summary', desc: 'Scorecard color counts summary' },
    { path: '/risc', desc: 'R.I.S.C. entries flattened — one row per category per tactical (R, I, S, C) with who / what / action taken', new: true },
    { path: '/risc-summary', desc: 'R.I.S.C. one row per tactical — all 12 fields as columns + completed-category count', new: true },
    { path: '/five-star-reviews', desc: '5-Star reviews with status + final score' },
    { path: '/five-star-categories', desc: 'Configured 5-Star categories' },
    { path: '/five-star-ratings', desc: 'Flat per-category ratings (manager / area_manager / final)' },
    { path: '/five-star-trend', desc: 'Per-employee historical scores' },
    { path: '/five-star-goals', desc: 'Goals captured at employee discussion step' },
    { path: '/five-star-action-items', desc: 'Action items captured at employee discussion step', new: true },
    { path: '/five-star-strengths-growth', desc: 'One row per Strength / Growth Area (manager · area_manager · final source)', new: true },
    { path: '/five-star-summaries', desc: 'Synthesis summary + employee comments + sign-off / discussed timestamps per review', new: true },
    { path: '/five-star-status-counts', desc: 'Status funnel counts (manager_rating, area_manager_review, …, discussed)', new: true },
    { path: '/five-star-am-history', desc: 'Audit trail of Area Manager reassignments, bulk transfers, and opt-outs', new: true },
    { path: '/five-star-activity-log', desc: 'Recent 5-Star events (initiations, deletes, restores, …) — supports ?limit=', new: true },
    { path: '/five-star-completion-summary', desc: 'Single-row KPI summary — totals, averages, avg days-to-complete', new: true },
  ];

  return (
    <div data-testid="tab-reports" className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="text-sm font-bold text-slate-900 mb-1">PowerBI Connection</div>
        <div className="text-xs text-slate-500 mb-4">All reports require the API key (passed as <code className="px-1 py-0.5 bg-slate-100 rounded text-[11px]">?api_key=...</code> query parameter).</div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between gap-3 p-2 bg-slate-50 rounded">
            <span className="text-slate-500">Base URL</span>
            <code className="text-slate-800 font-mono text-xs break-all">{baseUrl}</code>
          </div>
          <div className="flex justify-between gap-3 p-2 bg-slate-50 rounded">
            <span className="text-slate-500">API Key</span>
            <code className="text-slate-800 font-mono text-xs">{apiKey}</code>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
          <div className="text-sm font-bold">Available Endpoints</div>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200">
            <tr>
              <th className="text-left px-5 py-2 text-xs uppercase tracking-wide font-semibold text-slate-600">Endpoint</th>
              <th className="text-left px-5 py-2 text-xs uppercase tracking-wide font-semibold text-slate-600">Description</th>
            </tr>
          </thead>
          <tbody>
            {endpoints.map(ep => (
              <tr key={ep.path} className="border-b last:border-b-0 border-slate-100">
                <td className="px-5 py-2.5">
                  <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">GET /api/reports{ep.path}</code>
                  {ep.new && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">New</span>}
                </td>
                <td className="px-5 py-2.5 text-slate-700">{ep.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-600 flex items-start gap-2 p-3 bg-cyan-50 border border-cyan-200 rounded">
        <AlertCircle className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
        <span>
          Full request/response shapes, example URLs, and Power BI / Excel connection steps are in the{' '}
          <a href="/docs/user-manual.html#api-reference" target="_blank" rel="noopener noreferrer" className="font-semibold text-cyan-700 underline hover:text-cyan-900">User Manual → API Reference</a>{' '}
          (visible to Admin and Reports Admin roles).
        </span>
      </div>
    </div>
  );
};

// ============================ REVIEW INITIATORS TAB (item #6) ============================

const ReviewInitiatorsTab = () => {
  const [initiators, setInitiators] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [runningDigest, setRunningDigest] = useState(false);
  const [runningStaleSweep, setRunningStaleSweep] = useState(false);
  const [showGrant, setShowGrant] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase()}/api/admin-roles/review-initiators`, { headers: authHeaders() });
      const data = await res.json();
      setInitiators(data?.initiators || []);
      setLastSync(data?.last_sync || null);
    } catch (err) {
      toast.error('Failed to load review initiators');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch(`${apiBase()}/api/admin-roles/review-initiators/sync-entra`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Sync failed');
      setSyncResult(data);
      toast.success(`Sync complete: ${data.granted_new} new, ${data.already_had_role} already had role`);
      reload();
    } catch (err) {
      toast.error(err.message || 'Entra sync failed — check backend logs');
    } finally {
      setSyncing(false);
    }
  };

  const handleRunDigest = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Send the 5-Star weekly pending-reviews digest right now? One email will go to every user currently expected to act on a review.')) return;
    setRunningDigest(true);
    try {
      const res = await fetch(`${apiBase()}/api/admin-roles/five-star/weekly-digest/run-now`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Digest failed');
      toast.success('Digest sent — check the backend logs / Activity Log for per-recipient counts.');
    } catch (err) {
      toast.error(err.message || 'Digest failed — check backend logs');
    } finally {
      setRunningDigest(false);
    }
  };

  const handleRunStaleSweep = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Run the 5-Star stale-review sweep now?\n\n• Reviews ≥45 days old will trigger a warning email to the AM + Manager.\n• Reviews ≥60 days old will be auto-closed with status="expired".')) return;
    setRunningStaleSweep(true);
    try {
      const res = await fetch(`${apiBase()}/api/admin-roles/five-star/stale-sweep/run-now`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Stale sweep failed');
      toast.success('Sweep complete — check the 5-Star Activity Log for scanned / warned / expired counts.');
    } catch (err) {
      toast.error(err.message || 'Stale sweep failed — check backend logs');
    } finally {
      setRunningStaleSweep(false);
    }
  };

  const handleRevoke = async (userId, email) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove review-initiator role from ${email}?`)) return;
    try {
      await roleApi.revoke(userId, 'review_initiator');
      toast.success('Role revoked');
      reload();
    } catch (err) {
      toast.error(err.message || 'Failed to revoke');
    }
  };

  return (
    <div data-testid="tab-review-initiators" className="space-y-4">
      <div className="bg-gradient-to-br from-fuchsia-50/60 to-cyan-50/40 border border-fuchsia-200/60 rounded-xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-sm font-bold text-slate-900 mb-1 flex items-center gap-2">
              <Star className="w-4 h-4 text-fuchsia-600" />
              Review Initiators
            </div>
            <div className="text-xs text-slate-600 leading-relaxed max-w-xl">
              Users in this list can <strong>initiate 5-Star reviews</strong> and be selected as the <strong>Area Manager</strong>.
              They're auto-populated from two Entra ID groups (<code className="px-1 py-0.5 bg-white/70 rounded text-[10px]">DORCompanyWide</code> +
              <code className="px-1 py-0.5 bg-white/70 rounded text-[10px]">AMsCompanyWide</code>); you can also grant the role manually below.
            </div>
            <div className="mt-2 text-[11px] text-slate-500 inline-flex items-center gap-1.5" data-testid="last-sync-info">
              <Clock className="w-3 h-3" />
              {lastSync?.last_run_at
                ? <>Last sync: <strong className="text-slate-700">{fmtDate(lastSync.last_run_at)}</strong> ({lastSync.last_trigger || 'manual'} · +{lastSync.last_granted_new ?? 0} new)</>
                : <>Auto-sync runs daily at <strong className="text-slate-700">06:00</strong> — no run on record yet.</>
              }
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRunStaleSweep}
              disabled={runningStaleSweep}
              data-testid="run-stale-sweep-btn"
              title="Run the 5-Star stale-review sweep (45d warn + 60d auto-close). Normally runs daily 07:00."
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 shadow-sm disabled:opacity-60"
            >
              <AlertCircle className={`w-4 h-4 ${runningStaleSweep ? 'animate-pulse' : ''}`} />
              {runningStaleSweep ? 'Sweeping…' : 'Run Stale Sweep'}
            </button>
            <button
              onClick={handleRunDigest}
              disabled={runningDigest}
              data-testid="run-weekly-digest-btn"
              title="Trigger the 5-Star weekly pending-reviews digest right now (normally runs Mondays 08:00)"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border border-cyan-200 bg-white text-cyan-700 hover:bg-cyan-50 shadow-sm disabled:opacity-60"
            >
              <Mail className={`w-4 h-4 ${runningDigest ? 'animate-pulse' : ''}`} />
              {runningDigest ? 'Sending…' : 'Send Digest Now'}
            </button>
            <button
              onClick={handleSync}
              disabled={syncing}
              data-testid="entra-sync-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-slate-900 text-white shadow hover:bg-slate-800 disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing…' : 'Sync from Entra ID'}
            </button>
            <button
              onClick={() => setShowGrant(true)}
              data-testid="grant-initiator-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border border-slate-200 bg-white hover:bg-slate-50"
            >
              <Plus className="w-4 h-4" /> Add manually
            </button>
          </div>
        </div>

        {syncResult && (
          <div className="mt-4 p-4 rounded-lg bg-white border border-slate-200 text-xs space-y-2" data-testid="sync-result">
            <div className="font-bold text-slate-800 text-sm">
              Sync complete — {syncResult.granted_new} newly granted, {syncResult.already_had_role} already had role
            </div>
            <div className="space-y-1.5">
              {syncResult.groups?.map(g => (
                <div key={g.id} className={`pl-3 border-l-2 ${g.error ? 'border-rose-400' : 'border-cyan-400'}`}>
                  {g.error
                    ? <div className="text-rose-600">✗ <strong>{g.name}</strong>: {g.error}</div>
                    : (
                      <div className="space-y-0.5">
                        <div className="font-semibold text-slate-700">{g.name}</div>
                        <div className="text-slate-500">
                          {g.members_raw != null ? <><strong>{g.members_raw}</strong> in Entra group · </> : null}
                          <strong className="text-emerald-700">{g.members}</strong> active members
                          {g.disabled_or_guest_skipped > 0 && <> · <span className="text-amber-700">{g.disabled_or_guest_skipped} disabled/guest skipped</span></>}
                          {g.not_in_db > 0 && <> · <span className="text-rose-700">{g.not_in_db} not yet in app</span></>}
                          {' · '}<strong className="text-fuchsia-700">+{g.granted_new}</strong> granted
                        </div>
                      </div>
                    )}
                </div>
              ))}
            </div>
            {syncResult.users_not_in_db?.length > 0 && (
              <details className="pt-2 border-t border-slate-100">
                <summary className="cursor-pointer text-amber-700 font-semibold hover:text-amber-800">
                  ⚠ {syncResult.users_not_in_db.length} Entra users haven't signed in yet — click to view
                </summary>
                <div className="mt-2 max-h-64 overflow-y-auto bg-amber-50/40 rounded border border-amber-100 p-2 space-y-0.5">
                  {syncResult.users_not_in_db.map((u, i) => (
                    <div key={`${u.email}-${i}`} className="text-[11px] text-slate-700 flex justify-between gap-2">
                      <span className="font-medium truncate">{u.display_name || u.email}</span>
                      <span className="text-slate-500 truncate">{u.email}</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400">{u.group}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-[10px] text-slate-500 italic">
                  Tip: these accounts are in the Entra group but have never signed into the Growth Hub. They'll be granted the role automatically on their first login (or on the next 6 AM sync after they sign in).
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
          {loading ? 'Loading…' : `${initiators.length} review-initiator${initiators.length === 1 ? '' : 's'}`}
        </div>
        {!loading && initiators.length === 0 && (
          <div className="text-sm text-slate-500 italic">
            No initiators yet. Click <strong>Sync from Entra ID</strong> above to pull in the DOR + AM groups.
          </div>
        )}
        <div className="space-y-2">
          {initiators.map(u => (
            <div key={u.user_id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50" data-testid={`initiator-row-${u.email}`}>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-fuchsia-400 to-cyan-400 flex items-center justify-center text-white text-xs font-bold">
                {(u.display_name || u.email || '?').slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{u.display_name || u.email}</div>
                <div className="text-xs text-slate-500 truncate">{u.email}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${u.source === 'entra_sync' ? 'bg-cyan-50 text-cyan-700' : 'bg-slate-100 text-slate-600'}`}>
                {u.source === 'entra_sync' ? 'Entra sync' : 'Manual'}
              </span>
              <button
                type="button"
                onClick={() => handleRevoke(u.user_id, u.email)}
                data-testid={`revoke-initiator-${u.email}`}
                className="p-1.5 text-rose-500 hover:bg-rose-50 rounded"
                title="Remove role"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {showGrant && <GrantInitiatorDialog onClose={() => setShowGrant(false)} onGranted={reload} />}

      <BulkAreaManagerTransfer initiators={initiators} />
    </div>
  );
};

const BulkAreaManagerTransfer = ({ initiators }) => {
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [lookback, setLookback] = useState(1);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const peopleOptions = useMemo(
    () => (initiators || []).map(i => ({ id: i.user_id || i.id, email: i.email, display_name: i.display_name || i.email })),
    [initiators]
  );

  const handlePreview = async () => {
    if (!fromId || !toId) { toast.error('Pick both the outgoing and the replacement Area Manager'); return; }
    if (fromId === toId) { toast.error('Outgoing and replacement must be different people'); return; }
    setBusy(true); setResult(null);
    try {
      const res = await fiveStarApi.bulkTransferAreaManager({ fromUserId: fromId, toUserId: toId, lookbackYears: lookback, includeCompleted });
      setResult(res);
      toast.success(`Transferred ${res.transferred} review${res.transferred === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Transfer failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="am-transfer-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-cyan-600" />
            Transfer Area Manager Assignments
          </div>
          <div className="text-xs text-slate-500 mt-0.5 max-w-xl">
            When an Area Manager leaves, move all of their open reviews to a replacement in one click. Only in-flight reviews (not yet sign-off) within the chosen lookback window will move.
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">From (outgoing AM)</label>
          <EmployeeTypeahead value={fromId} onChange={setFromId} options={peopleOptions} placeholder="Search outgoing AM…" testId="am-transfer-from" />
        </div>
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">To (replacement AM)</label>
          <EmployeeTypeahead value={toId} onChange={setToId} options={peopleOptions} placeholder="Search replacement…" testId="am-transfer-to" />
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4 flex-wrap text-xs text-slate-600">
        <label className="inline-flex items-center gap-2">
          <span className="font-semibold">Lookback:</span>
          <select value={lookback} onChange={(e) => setLookback(Number(e.target.value))} data-testid="am-transfer-lookback" className="px-2 py-1 rounded border border-slate-300 text-xs">
            <option value={1}>1 year</option>
            <option value={3}>3 years</option>
          </select>
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={includeCompleted} onChange={(e) => setIncludeCompleted(e.target.checked)} data-testid="am-transfer-include-completed" className="rounded border-slate-300" />
          <span>Include completed (signed-off / discussed) reviews</span>
        </label>
      </div>

      <button
        type="button"
        onClick={handlePreview}
        disabled={busy || !fromId || !toId || fromId === toId}
        data-testid="am-transfer-submit"
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-cyan-600 text-white shadow hover:bg-cyan-700 disabled:opacity-50"
      >
        <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
        {busy ? 'Transferring…' : 'Transfer reviews'}
      </button>

      {result && (
        <div className="mt-4 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs" data-testid="am-transfer-result">
          <div className="font-bold text-slate-800 mb-1">
            {result.transferred} review{result.transferred === 1 ? '' : 's'} moved to <span className="text-cyan-700">{result.to}</span>
          </div>
          <div className="text-slate-500">Lookback {result.lookback_years}y · include_completed={String(result.include_completed)}</div>
          {result.reviews?.length > 0 && (
            <ul className="mt-2 max-h-44 overflow-y-auto space-y-0.5">
              {result.reviews.slice(0, 30).map(r => (
                <li key={r.id} className="text-[11px] text-slate-600">
                  · {r.employee_email} <span className="text-slate-400">({r.cycle_name}, status={r.status})</span>
                </li>
              ))}
              {result.reviews.length > 30 && <li className="text-[11px] text-slate-400 italic">…and {result.reviews.length - 30} more.</li>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const GrantInitiatorDialog = ({ onClose, onGranted }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [granting, setGranting] = useState('');

  useEffect(() => {
    if (q.length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const data = await roleApi.search(q);
        setResults(Array.isArray(data) ? data : (data?.users || []));
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const handleGrant = async (u) => {
    setGranting(u.id);
    try {
      await roleApi.grant(u.id, 'review_initiator');
      toast.success(`${u.display_name || u.email} can now initiate reviews`);
      onGranted();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to grant role');
    } finally {
      setGranting('');
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900">Grant Review-Initiator Role</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" type="button"><X className="w-4 h-4" /></button>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            autoFocus
            data-testid="grant-search-input"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-fuchsia-400"
          />
        </div>
        <div className="mt-3 max-h-72 overflow-auto space-y-1">
          {q.length < 2 && (
            <div className="text-xs text-slate-400 py-3 text-center italic">Type at least 2 characters…</div>
          )}
          {q.length >= 2 && results.length === 0 && (
            <div className="text-xs text-slate-400 py-3 text-center italic">No users found</div>
          )}
          {results.map(u => (
            <button
              key={u.id}
              type="button"
              onClick={() => handleGrant(u)}
              disabled={granting === u.id}
              data-testid={`grant-result-${u.email}`}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-100 hover:bg-slate-50 hover:border-slate-200 disabled:opacity-50 text-left"
            >
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-fuchsia-400 to-cyan-400 flex items-center justify-center text-white text-[10px] font-bold">
                {(u.display_name || u.email || '?').slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{u.display_name}</div>
                <div className="text-xs text-slate-500 truncate">{u.email}</div>
              </div>
              {granting === u.id ? <span className="text-xs text-slate-400">Granting…</span> : <Plus className="w-4 h-4 text-fuchsia-500" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================ MAIN PAGE ============================

const AdminHub = () => {
  const { user } = useAuth();
  const [myRoles, setMyRoles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('my-roles');

  useEffect(() => {
    (async () => {
      try {
        const data = await roleApi.me();
        setMyRoles(data);
      } catch (err) {
        toast.error('Failed to load admin roles');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="px-12 py-10 text-sm text-slate-500">Loading admin…</div>;

  const roles = myRoles?.roles || [];
  const isSuper = roles.includes('super_admin');
  const canTemplate = isSuper || roles.includes('template_admin');
  const canReports = isSuper || roles.includes('reports_admin');

  if (roles.length === 0) {
    return (
      <div className="px-12 py-10 max-w-2xl" data-testid="admin-no-access">
        <Toaster position="top-right" richColors />
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <Shield className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <div className="text-lg font-bold text-slate-900 mb-1">No Admin Access</div>
          <div className="text-sm text-slate-500">
            You don't currently hold any admin role. Contact the super admin if you believe this is an error.
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'my-roles', label: 'My Roles', icon: UserCog, visible: true },
    { key: 'user-roles', label: 'User Roles', icon: Crown, visible: isSuper },
    { key: 'review-initiators', label: 'Review Initiators', icon: Star, visible: isSuper },
    { key: 'templates', label: 'Templates', icon: FileText, visible: canTemplate },
    { key: 'month-preview', label: 'Month Preview', icon: CalendarClock, visible: isSuper },
    { key: 'whats-new', label: "What's New", icon: Bell, visible: isSuper },
    { key: 'analytics', label: 'Analytics', icon: BarChart3, visible: isSuper },
    { key: 'activity', label: 'Activity', icon: Activity, visible: isSuper },
    { key: 'invites', label: 'Invites', icon: UserPlus, visible: isSuper },
    { key: 'impersonate', label: 'Impersonate', icon: Eye, visible: isSuper },
    { key: 'reports', label: 'Reports / PowerBI', icon: Download, visible: canReports },
  ].filter(t => t.visible);

  // Build role badges with interleaved spaces — cheap for ≤3 roles, no hook needed
  const roleBadges = roles.flatMap((r, i) =>
    i === 0 ? [<RoleBadge key={r} role={r} />] : [<span key={`sp-${r}`}> </span>, <RoleBadge key={r} role={r} />]
  );

  return (
    <div className="px-12 pt-10 pb-20 max-w-[1400px]" data-testid="admin-hub">
      <Toaster position="top-right" richColors />
      <div className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-[1px] text-slate-400 mb-1.5">Administration</div>
        <h1 className="text-[28px] font-extrabold tracking-tight text-slate-900">Admin Hub</h1>
        <p className="text-sm text-slate-500 mt-1">
          Welcome, <strong className="text-slate-700">{user?.display_name}</strong>. Your roles: {roleBadges}
        </p>
      </div>

      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-lg w-fit">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-md transition ${tab === t.key ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-800'}`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'my-roles' && <MyRolesTab myRoles={myRoles} />}
      {tab === 'user-roles' && isSuper && <UserRolesTab />}
      {tab === 'review-initiators' && isSuper && <ReviewInitiatorsTab />}
      {tab === 'templates' && canTemplate && <TemplatesTab />}
      {tab === 'month-preview' && isSuper && <MonthPreviewHubTab />}
      {tab === 'whats-new' && isSuper && <WhatsNewHubTab />}
      {tab === 'analytics' && isSuper && <AnalyticsHubTab />}
      {tab === 'activity' && isSuper && <ActivityHubTab />}
      {tab === 'invites' && isSuper && <InvitesHubTab />}
      {tab === 'impersonate' && isSuper && <ImpersonateHubTab />}
      {tab === 'reports' && canReports && <ReportsTab />}
    </div>
  );
};

export default AdminHub;
